// src/views/AIManagerModule.jsx
import React, { useState, useEffect } from 'react';

const AI_ASSISTANT_URL = process.env.REACT_APP_AIASSISTANT_URL;

export default function AIManagerModule() {
  const [activeTab, setActiveTab] = useState('assistant');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I am your local AI assistant. How can I help you manage your platform today?',
    },
  ]);
  const [inputPrompt, setInputPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('qwen2.5:1.5b');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // --- Training Form State ---
  const [modelName, setModelName] = useState('qwen2.5-custom');
  const [systemPrompt, setSystemPrompt] = useState('You are a helpful administration assistant.');
  const [temperature, setTemperature] = useState(0.3);
  const [trainingPairs, setTrainingPairs] = useState([
    { instruction: '', output: '' },
  ]);

  // --- Knowledge & Cache Management State ---
  const [knowledgeList, setKnowledgeList] = useState([
    { fact_key: 'Founder', fact_value: 'Nadya Taylor' },
    { fact_key: 'Organization', fact_value: 'RPSwiss (Association of Russian-speaking Professionals in Switzerland)' },
  ]);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  // Available models list
  const [availableModels, setAvailableModels] = useState([
    'qwen2.5:1.5b',
    'rp_swiss_llm',
  ]);

  // Dynamic pair manipulation (Training)
  const handleAddPair = () => {
    setTrainingPairs((prev) => [...prev, { instruction: '', output: '' }]);
  };

  const handleRemovePair = (index) => {
    setTrainingPairs((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handlePairChange = (index, field, value) => {
    setTrainingPairs((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // --- Knowledge Base Handlers ---
  const handleAddFact = (e) => {
    e.preventDefault();
    if (!newKey.trim() || !newValue.trim()) {
      setError('Both Fact Key and Fact Value are required.');
      return;
    }

    setKnowledgeList((prev) => [...prev, { fact_key: newKey.trim(), fact_value: newValue.trim() }]);
    setNewKey('');
    setNewValue('');
    setSuccessMsg('Fact added locally. Remember to click "Sync Knowledge to DB & Flush Cache"');
  };

  const handleRemoveFact = (index) => {
    setKnowledgeList((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleKnowledgeJsonUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target.result.trim();
        const json = JSON.parse(content);
        let importedFacts = [];

        if (Array.isArray(json)) {
          importedFacts = json.map((item) => ({
            fact_key: item.fact_key || item.key || item.instruction || 'Key',
            fact_value: item.fact_value || item.value || item.output || 'Value',
          }));
        } else if (typeof json === 'object') {
          importedFacts = Object.entries(json).map(([k, v]) => ({
            fact_key: k,
            fact_value: typeof v === 'object' ? JSON.stringify(v) : String(v),
          }));
        }

        if (importedFacts.length === 0) {
          throw new Error('No valid key-value facts found in file.');
        }

        setKnowledgeList((prev) => [...prev, ...importedFacts]);
        setSuccessMsg(`Successfully imported ${importedFacts.length} fact(s) from ${file.name}`);
      } catch (err) {
        setError('Failed to parse Knowledge JSON: ' + err.message);
      }
    };

    reader.readAsText(file);
  };

  const handleSaveKnowledgeAndFlushCache = async () => {
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      const response = await fetch(AI_ASSISTANT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'update_knowledge',
          knowledge: knowledgeList,
          clearCache: true,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || `HTTP error! Status: ${response.status}`);
      }

      setSuccessMsg(data.message || 'Knowledge Base successfully updated and cache invalidated.');
    } catch (err) {
      setError(err.message || 'Failed to update Knowledge Base.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearCacheOnly = async () => {
    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      const response = await fetch(AI_ASSISTANT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'clear_cache',
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || `HTTP error! Status: ${response.status}`);
      }

      setSuccessMsg(data.message || 'APCu / File Cache purged successfully.');
    } catch (err) {
      setError(err.message || 'Failed to flush cache.');
    } finally {
      setLoading(false);
    }
  };

  // --- Dataset Upload Handler (Training) ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError('');
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const content = event.target.result.trim();
        let parsedData = [];

        if (file.name.endsWith('.jsonl')) {
          const lines = content.split('\n').filter((line) => line.trim().length > 0);
          parsedData = lines.map((line) => JSON.parse(line));
        } else {
          parsedData = JSON.parse(content);
        }

        if (!Array.isArray(parsedData)) {
          throw new Error('Dataset file must contain a JSON array or valid JSONL lines.');
        }

        const formattedPairs = parsedData.map((item) => ({
          instruction: item.instruction || item.prompt || '',
          output: item.output || item.response || '',
        }));

        setTrainingPairs(formattedPairs);
        setSuccessMsg(`Successfully imported ${formattedPairs.length} training pair(s) from ${file.name}`);
      } catch (err) {
        setError('Failed to parse uploaded dataset file: ' + err.message);
      }
    };

    reader.readAsText(file);
  };

  // Submit Training Dataset
  const handleTrainSubmit = async (e) => {
    e.preventDefault();
    const cleanName = modelName.trim().toLowerCase().replace(/[^a-z0-9_.-]/g, '');

    if (!cleanName) {
      setError('Model name is required.');
      return;
    }

    const validPairs = trainingPairs.filter(
      (pair) => pair.instruction.trim() !== '' && pair.output.trim() !== ''
    );

    if (validPairs.length === 0) {
      setError('At least one non-empty instruction/output pair is required.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMsg('');

    try {
      const response = await fetch(AI_ASSISTANT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'train',
          modelName: cleanName,
          systemPrompt: systemPrompt.trim(),
          temperature: parseFloat(temperature),
          trainingData: validPairs,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || `HTTP error! Status: ${response.status}`);
      }

      if (!availableModels.includes(cleanName)) {
        setAvailableModels((prev) => [...prev, cleanName]);
      }
      setSelectedModel(cleanName);

      setSuccessMsg(data.message || `Model '${cleanName}' trained and deployed successfully.`);
    } catch (err) {
      setError(err.message || 'Failed to submit training request.');
    } finally {
      setLoading(false);
    }
  };

  // --- Chat Handlers ---
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputPrompt.trim() || loading) return;

    const userMessage = inputPrompt.trim();
    setInputPrompt('');
    setError('');

    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch(AI_ASSISTANT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          action: 'chat',
          model: selectedModel,
          prompt: userMessage,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        throw new Error(data.error || `HTTP error! Status: ${response.status}`);
      }

      const assistantReply = data.result || data.response || 'No response returned from assistant.';
      setMessages((prev) => [...prev, { role: 'assistant', content: assistantReply }]);
    } catch (err) {
      setError(err.message || 'Failed to connect to local AI assistant endpoint.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setError('');
  };

  return (
    <div className="admin-view-wrapper">
      {/* 1. Header Area */}
      <div className="admin-view-header">
        <h2 className="admin-title">AI Manager</h2>
        {error && <div className="admin-error">{error}</div>}
        {successMsg && (
          <div
            className="admin-error"
            style={{
              backgroundColor: 'rgba(34, 197, 94, 0.15)',
              borderColor: '#22c55e',
              color: '#86efac',
            }}
          >
            {successMsg}
          </div>
        )}
      </div>

      {/* 2. Secondary Navigation Tabs */}
      <div className="admin-table-toolbar" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px' }}>
        <div className="btn-actions-container" style={{ gap: '8px' }}>
          <button
            type="button"
            className={`btn ${activeTab === 'assistant' ? 'btn-submit-members' : 'btn-cancel'}`}
            onClick={() => { setActiveTab('assistant'); setError(''); setSuccessMsg(''); }}
          >
            Chat Assistant
          </button>
          <button
            type="button"
            className={`btn ${activeTab === 'knowledge' ? 'btn-submit-members' : 'btn-cancel'}`}
            onClick={() => { setActiveTab('knowledge'); setError(''); setSuccessMsg(''); }}
          >
            DB Knowledge & Cache
          </button>
          <button
            type="button"
            className={`btn ${activeTab === 'training' ? 'btn-submit-members' : 'btn-cancel'}`}
            onClick={() => { setActiveTab('training'); setError(''); setSuccessMsg(''); }}
          >
            Fine-Tuning & Datasets
          </button>
          <button
            type="button"
            className={`btn ${activeTab === 'config' ? 'btn-submit-members' : 'btn-cancel'}`}
            onClick={() => { setActiveTab('config'); setError(''); setSuccessMsg(''); }}
          >
            Model Configuration
          </button>
        </div>
      </div>

      {/* 3. Chat Workspace */}
      {activeTab === 'assistant' && (
        <div className="admin-form-container" style={{ display: 'flex', flexDirection: 'column', height: '600px', padding: '0' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              justify: 'space-between',
              alignItems: 'center',
              backgroundColor: 'rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>Active Model:</label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#000000',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                {availableModels.map((m) => (
                  <option key={m} value={m} style={{ backgroundColor: '#1e293b', color: '#ffffff' }}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
              Target: {selectedModel}
            </span>
          </div>

          <div
            className="chat-messages-container"
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {messages.length === 0 ? (
              <div style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginTop: 'auto', marginBottom: 'auto' }}>
                No active messages. Type a prompt below to begin chatting.
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '80%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: msg.role === 'user' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                    border: msg.role === 'user' ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.08)',
                    color: '#000000',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <div
                    style={{
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      color: msg.role === 'user' ? '#86efac' : 'rgba(255,255,255,0.5)',
                      marginBottom: '4px',
                    }}
                  >
                    {msg.role === 'user' ? 'Admin' : `AI Assistant (${selectedModel})`}
                  </div>
                  {msg.content}
                </div>
              ))
            )}
            {loading && (
              <div
                style={{
                  alignSelf: 'flex-start',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '14px',
                }}
              >
                Thinking using {selectedModel}...
              </div>
            )}
          </div>

          <form
            onSubmit={handleSendMessage}
            style={{
              padding: '16px',
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
            }}
          >
            <input
              type="text"
              placeholder={`Ask ${selectedModel}...`}
              value={inputPrompt}
              onChange={(e) => setInputPrompt(e.target.value)}
              disabled={loading}
              style={{
                flex: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                color: '#000000',
                padding: '12px 16px',
                fontSize: '14px',
                borderRadius: '4px',
                outline: 'none',
              }}
            />
            <button type="submit" className="btn btn-submit-members" disabled={loading || !inputPrompt.trim()}>
              {loading ? 'Sending...' : 'Send'}
            </button>
            <button type="button" className="btn btn-cancel" onClick={handleClearChat} disabled={loading}>
              Clear
            </button>
          </form>
        </div>
      )}

      {/* 4. DB Knowledge & Cache Management Tab */}
      {activeTab === 'knowledge' && (
        <div className="admin-form-container grid-2-col">
          <div className="form-section-header" style={{ gridColumn: 'span 2' }}>
            <h4 className="form-section-title">Knowledge Base & System Context Cache</h4>
          </div>

          {/* Quick Cache Control */}
          <div
            style={{
              gridColumn: 'span 2',
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '6px',
              padding: '16px',
              display: 'flex',
              justify: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                Context Invalidation & Cache Flusher
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
                Purges active APCu or local file memory caches to force rebuild of system context from database.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-cancel"
              style={{ color: '#f59e0b', borderColor: '#f59e0b' }}
              onClick={handleClearCacheOnly}
              disabled={loading}
            >
              Flush Cache
            </button>
          </div>

          {/* Add Name/Value Pair */}
          <form onSubmit={handleAddFact} style={{ gridColumn: 'span 2', display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Fact Key / Category *</label>
              <input
                type="text"
                placeholder="e.g. Founder"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Fact Value *</label>
              <input
                type="text"
                placeholder="e.g. Nadya Taylor"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
              />
            </div>
            <button type="submit" className="btn btn-submit-members" style={{ height: '42px' }}>
              + Add Fact
            </button>
          </form>

          {/* Bulk Import JSON file */}
          <div className="form-section-divider" style={{ gridColumn: 'span 2', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 className="form-section-title">Import Bulk Knowledge JSON</h4>
              <label className="btn btn-cancel" style={{ cursor: 'pointer', padding: '6px 12px', fontSize: '13px' }}>
                Upload JSON File
                <input
                  type="file"
                  accept=".json"
                  onChange={handleKnowledgeJsonUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </div>

          {/* Table / List of Knowledge items */}
          <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {knowledgeList.map((item, idx) => (
              <div
                key={idx}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.04)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: '4px',
                  padding: '10px 14px',
                  display: 'flex',
                  justify: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <span style={{ fontWeight: '600', color: '#86efac', marginRight: '8px' }}>
                    {item.fact_key}:
                  </span>
                  <span style={{ color: '#ffffff', fontSize: '14px' }}>{item.fact_value}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-cancel"
                  style={{ padding: '2px 8px', fontSize: '12px', color: '#ef4444' }}
                  onClick={() => handleRemoveFact(idx)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="btn-actions-container" style={{ gridColumn: 'span 2', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              type="button"
              className="btn btn-submit-members"
              onClick={handleSaveKnowledgeAndFlushCache}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Sync Knowledge to DB & Flush Cache'}
            </button>
          </div>
        </div>
      )}

      {/* 5. Fine-Tuning & Datasets Tab */}
      {activeTab === 'training' && (
        <form onSubmit={handleTrainSubmit} className="admin-form-container grid-2-col">
          <div className="form-section-header" style={{ gridColumn: 'span 2' }}>
            <h4 className="form-section-title">Model Variant & Parameters</h4>
          </div>

          <div className="form-group">
            <label>Target Model Name *</label>
            <input
              type="text"
              placeholder="e.g. qwen2.5-custom"
              required
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Temperature (0.0 - 1.0)</label>
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
            />
          </div>

          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>System Instructions</label>
            <input
              type="text"
              placeholder="e.g. You are a platform administrator assistant for RPSwiss."
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
            />
          </div>

          <div className="form-section-divider" style={{ gridColumn: 'span 2', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 className="form-section-title">Training Dataset (1:N Instructions)</h4>

              <div>
                <label className="btn btn-cancel" style={{ cursor: 'pointer', padding: '6px 12px', fontSize: '13px' }}>
                  Import JSON / JSONL
                  <input
                    type="file"
                    accept=".json,.jsonl"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          </div>

          {trainingPairs.map((pair, index) => (
            <div
              key={index}
              style={{
                gridColumn: 'span 2',
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '6px',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>
                  Pair #{index + 1}
                </span>
                {trainingPairs.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemovePair(index)}
                    className="btn btn-cancel"
                    style={{ padding: '2px 8px', fontSize: '12px', color: '#ef4444' }}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="form-group">
                <label>Instruction / User Input *</label>
                <input
                  type="text"
                  placeholder="e.g. Who is the founder of RPSwiss?"
                  value={pair.instruction}
                  onChange={(e) => handlePairChange(index, 'instruction', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Expected Model Output *</label>
                <textarea
                  placeholder="e.g. The founder of RPSwiss is Nadya Taylor."
                  value={pair.output}
                  onChange={(e) => handlePairChange(index, 'output', e.target.value)}
                  rows="2"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    color: '#000000',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>
          ))}

          <div
            className="btn-actions-container"
            style={{ gridColumn: 'span 2', justifyContent: 'space-between', marginTop: '12px' }}
          >
            <button type="button" onClick={handleAddPair} className="btn btn-cancel">
              + Add Instruction Pair
            </button>
            <button type="submit" className="btn btn-submit-members" disabled={loading}>
              {loading ? 'Creating Model...' : 'Build Custom Model'}
            </button>
          </div>
        </form>
      )}

      {/* 6. Model Configuration Tab */}
      {activeTab === 'config' && (
        <div className="admin-form-container" style={{ padding: '32px', textAlign: 'center' }}>
          <h4 className="form-section-title" style={{ marginBottom: '12px' }}>Model Configuration</h4>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
            System prompts, temperature parameters, context length limits, and endpoint host overrides will be configurable here.
          </p>
        </div>
      )}
    </div>
  );
}